<template>
  <div>
    <CommonAlert v-if="error" type="error" :content="error.message" class="mb-6" />
    <form @submit.prevent="onSubmit" class="mx-auto">
      <div class="w-full">
        <h1 class="sr-only">
          {{ $t("pages.account.signup.title") }}
        </h1>

        <Transition :name="transitionName">
          <div v-if="progressiveTabStep >= 0" v-show="activeStep === 0">
            <div class="flex flex-col md:gap-4 md:pt-6">
              <BaseVTextInput
                type="text"
                :label="`${$t('common.fields.email')}`"
                name="email"
              />

              <div class="mt-2 flex flex-col">
                <BaseVTextInput
                  type="password"
                  :label="`${$t('common.fields.password')}`"
                  name="password"
                />
                <span class="flex flex-row items-center gap-2 mt-2 text-sm">
                  <CInfoE class="text-[1em]" /> {{ $t('pages.account.signup.texts.password_requirements') }}
                </span>
              </div>

              <BaseVTextInput
                type="password"
                :label="`${$t('common.fields.password_confirmation')}`"
                name="password_confirmation"
                class="mt-3"
              />
            </div>
            <BaseButton
              type="submit"
              :text="`${$t('buttons.common.next_step')}`"
              class="w-full h-11 text-lg mt-4 mb-8"
            />

            <div class="border-b w-full mt-4"></div>
            <BaseTypography variant="p" class="text-center text-sm font-medium pt-4 px-2 md:pb-1">
              {{ $t("pages.account.signup.texts.alread_have_an_account") }}
              <NuxtLinkLocale :locale="switchBlogLocale()" to="/account/login"
                class="text-secondary underline hover:no-underline">
                {{ $t("buttons.common.login") }}
              </NuxtLinkLocale>
            </BaseTypography>
          </div>
        </Transition>

        <Transition :name="transitionName">
          <div v-if="progressiveTabStep >= 1" v-show="activeStep === 1">
            <div class="flex flex-row flex-wrap gap-x-3 md:gap-6 md:pt-6">
              <BaseVTextInput
                type="text"
                :label="`${$t('common.fields.first_name')}`"
                name="first_name"
                class="flex-1 basis-full"
              />
              <BaseVTextInput
                type="text"
                :label="`${$t('common.fields.last_name')}`"
                name="last_name"
                class="flex-1 basis-full"
              />
              <BaseVTextInput
                type="text"
                :label="`${$t('common.fields.date_of_birth')}`"
                input-mask="##/##/####"
                placeholder="DD/MM/YYYY"
                name="date_of_birth"
                class="flex-1 basis-1/3 [&>input]:h-[40px]"
              />

              <BaseVSelect
                name="gender"
                class="flex-1 basis-1/3"
                :placeholder="`${$t('common.fields.gender')}`"
                :label="$t('common.fields.gender')"
                :options="[
                  {
                    text: $t('pages.account.signup.gender.male'),
                    value: 'M',
                  },
                  {
                    text: $t('pages.account.signup.gender.female'),
                    value: 'F',
                  },
                  {
                    text: $t('pages.account.signup.gender.other'),
                    value: 'U',
                  },
                ]"
              />
            </div>

            <div class="mt-7 flex flex-row gap-3 items-baseline">
              <BaseCheckboxInput name="accept_terms_and_conditions"
                @change="(state: boolean) => (userAcceptedTermsAndConditions = state)" checkbox-class="self-baseline"
                :size="1.125">
                {{
                  $t(
                    "pages.account.signup.texts.i_have_already_read_and_accept_the"
                  )
                }}
                <NuxtLinkLocale :locale="switchBlogLocale()" :to="termsAndConditionsUrl"
                  class="underline text-secondary hover:no-underline" target="_blank">
                  {{ $t("commons.texts.terms_&_conditions") }}
                </NuxtLinkLocale>
                {{
                  $t(
                    "pages.account.signup.texts.of_this_service_in_addition_i_confirm_that_i_am_aged_18_years_or_older"
                  )
                }}
              </BaseCheckboxInput>
            </div>

            <div class="flex flex-row gap-3 mt-8">
              <BaseButton variant="text" class="basis-1/3 text-secondary" @click="activeStep -= 1">
                {{ $t("buttons.common.back") }}
              </BaseButton>
              <BaseButton type="submit" :text="$t('buttons.common.create_account')" class="flex-1 h-11 text-lg"
                :loading="pending" :disabled="!userAcceptedTermsAndConditions" />
            </div>
          </div>
        </Transition>

        <Transition :name="transitionName">
          <div v-if="progressiveTabStep >= 2" v-show="activeStep === 2" class="px-3">
            <div class="text-center flex flex-col items-center mt-3">
              <CheckCircleIcon class="text-primary text-3xl w-[1em]" />
              <BaseTypography disable-ellipsis class="font-semibold mt-1">
                {{ $t("pages.account.signup.texts.account_created") }}
              </BaseTypography>
            </div>

            <BaseTypography disable-ellipsis class="mt-5 mb-3">
              {{ $t("pages.account.signup.texts.verification_email_sent") }}
            </BaseTypography>
            <div class="bg-[#D8D8D84D] p-5 text-center font-semibold rounded-lg text-lg">
              {{ form.values.email ?? "?@?" }}
            </div>

            <BaseTypography disable-ellipsis class="mt-7">
              {{ $t("pages.account.signup.texts.click_email_link") }}
            </BaseTypography>
            <NuxtLinkLocale :locale="switchBlogLocale()" to="#"
              class="block mt-2 mb-4 underline text-secondary hover:no-underline cursor-pointer w-fit" :class="{
                'pointer-events-none opacity-50':
                  resendingEmailVerification || !canResendEmailVerification,
              }" @click.prevent="resendEmailVerification">
              {{ $t("pages.account.signup.buttons.resend_activation_email") }}
            </NuxtLinkLocale>
          </div>
        </Transition>
      </div>
    </form>
  </div>
</template>

<script setup lang="ts">
import dayjs from "@/utils/dayjs";
import { CheckCircleIcon } from "@heroicons/vue/solid";
import { AxiosError } from "axios";
import { configure, useForm } from "vee-validate";
import * as yup from "yup";
import CInfoE from '~/components/icons/CIconE.vue';

configure({
  validateOnBlur: false,
  validateOnChange: false,
  validateOnInput: false,
  validateOnModelUpdate: false,
});

const globalStore = useGlobalStore();
const { t } = useI18n();

const hasPreviousStep = ref<boolean>(false);
const activeStep = ref<number>(0);
const progressiveTabStep = ref(activeStep.value);

const userAcceptedTermsAndConditions = ref<boolean>(false);

const countryDateFormat = computed(
  () => globalStore.siteSettings.time_and_date.frontend_date_format
);
const minAge = computed(
  () =>
    globalStore.globalData?.min_booking_age ??
    0
);

const schemas = [
  // 1. User credentials step
  {
    email: yup.string().email().required().default(""),
    password: yup.string().required().default(""),
    password_confirmation: yup
      .string()
      .oneOf([yup.ref("password")], t("pages.account.signup.errors.passwords_mismatch")),
  },

  // 2. Person informations step
  {
    first_name: yup.string().required().default(""),
    last_name: yup.string().required().default(""),
    date_of_birth: yup.date()
      .typeError(
        t("validationErrors.dateOfBirthTypeError", {
          dateFormat: countryDateFormat.value,
        })
      )
      .nullable()
      .required(t("validationErrors.dateOfBirthRequired"))
      .transform((_, value) =>
        dayjs(value, countryDateFormat.value, true).toDate()
      )
      .test(
        "valid date format",
        t("validationErrors.dateOfBirthTypeError", {
          dateFormat: countryDateFormat.value,
        }),
        (value) => dayjs(value).isValid()
      )
      .test(
        "age_restriction",
        t("validationErrors.dateOfBirthMinAge", { minAge: minAge.value }),
        (birthDay) => {
          const minDate = dayjs().subtract(minAge.value, "years");
          const isOldEnough = dayjs(birthDay).isBefore(minDate);
          return isOldEnough;
        }
      ),
    gender: yup.string().oneOf(["M", "F", "U"]).required().default(""),
  },
];
const formSchema = schemas.reduce(
  (prevSchema, currentSchema) =>
    prevSchema.concat(
      // @ts-ignore
      yup.object(currentSchema)
    ),
  yup.object({})
);

const error = ref<Error>();

const termsAndConditionsUrl = computed(() => {
  const uri = localeUri(`/legal-and-privacy/terms-conditions`)
  const baseUrl = "https://www.better2know.com";
  return baseUrl + uri.fullPath;
});

interface RegisterResponse {
  data: {
    first_name: string;
    last_name: string;
    id: number;
    verification_token: string;
  };
}
const {
  data: registeredAccount,
  mutate,
  pending,
} = useMutation({
  mutationFn: async (account: object) =>
    useAxios()
      .postForm<RegisterResponse>("/register", account)
      .then((res) => res.data.data),
  onError(err) {
    error.value = err;
    if (err instanceof AxiosError)
      error.value = new Error(err.response?.data.message);
  },
  onSuccess() {
    error.value = undefined;
    activeStep.value += 1;
  },
});

const form = useForm({
  initialValues: formSchema.getDefault(),
  validationSchema: formSchema,
});

function onSubmit(e: Event) {
  // Temporary fix for terms and condition check request
  if (activeStep.value === 1 && !userAcceptedTermsAndConditions.value) return;

  const fd = new FormData(e.target as HTMLFormElement);
  const data = Object.fromEntries(fd.entries());

  // 1. Validate the current step
  const isFinalStep = activeStep.value >= schemas.length - 1;
  const currentStepSchema = schemas[activeStep.value];

  try {
    yup
      .object(
        // @ts-ignore
        currentStepSchema ?? {}
      )
      .validateSync(data, {
        stripUnknown: true,
        abortEarly: false,
      });
  } catch (err) {
    if (err instanceof yup.ValidationError !== true) throw err;
    return form.validate({
      mode: "force",
    });
  }

  // 2. Submit form with current step changes.
  if (isFinalStep) return mutate(data);

  // 3. Go to next step
  activeStep.value += 1;
}

watch(activeStep, (current, prev) => {
  if (current < prev) return;
  progressiveTabStep.value = current;
});

const scrollToView = () => {
  window.scrollTo({
    top: 0,
    //behavior: "smooth",
  });
};

watch(activeStep, (newVal, prevVal) => {
  hasPreviousStep.value = newVal > prevVal;
  scrollToView();
});
const transitionName = computed(() =>
  !hasPreviousStep.value ? "slide-fade-back" : "slide-fade"
);

onMounted(() => {
  scrollToView();
});

const {
  mutate: resendEmailVerification,
  pending: resendingEmailVerification,
  canExecute: canResendEmailVerification,
} = useMutation({
  mutationFn: async () =>
    useAxios()
      .get(
        `/email/verification-notification/${registeredAccount.value?.id}/${registeredAccount.value?.verification_token}`
      )
      .then((res) => res.data),
  executionInterval: 1, // Wait at least 1 minute before allowing the user to resend email verification
  executionIntervalUnit: "minutes",
});
</script>

<style>
.slide-fade-enter-active {
  transition: transform 0.3s ease-in-out;
  z-index: 2;
  transform: translateX(100%);
}

.slide-fade-enter-to {
  transform: translateX(0%);
  z-index: 2;
}

.slide-fade-back-enter-active {
  transition: transform 0.3s ease-in-out;
  z-index: 2;
  transform: translateX(-100%);
}

.slide-fade-back-enter-to {
  transform: translateX(0%);
  z-index: 2;
}
</style>
